import { Container, Graphics, Rectangle, Text } from "pixi.js"
import TextInput from "pixi-text-input"
import { ENTITY_NAME_MAX_CHARS } from "game-common/entity_name"
import { TextRenderer } from "../text/text_renderer"
import { Fader } from "./fader"

// The name prompt: a small field that floats over a camp member so the player can name it.
//
// Put up by the server (Protocol.type_openNamePrompt) at the two moments naming belongs to -- the
// end of a hire, as the last beat of the role picker, and the "Rename" order from a member's
// command wheel. It always opens on a name the member ALREADY has (one rolled from the camp's
// register, server-side), so the player is editing rather than filling in a blank, and dismissing
// it keeps that name. Nothing waits on the answer.
//
// Styled and driven exactly like role_picker.ts beside it -- same ember-lit palette, same
// bind/fade API so GuiManager can treat them the same way. Unlike the picker it does NOT follow its
// entity: it is pinned where it opened, because a field that slides around under a cursor (and a
// soft keyboard) is hard to type into. Same reasoning as the command wheel staying put.
//
// The text field is pixi-text-input, i.e. a real DOM <input> positioned over the canvas, which is
// what makes it work with a phone's keyboard. The two departures from the library's defaults are
// the same ones intro/login_field.ts documents at length (substituteText off, our own box), and
// are repeated rather than shared because that class is wired into the intro's own theme.

// Fade timing -- mirrors RolePicker / CommandWheel (~100ms each way).
const FADE_INTERVAL_MS = 5
const FADE_STEP = 0.05

// Ember-lit palette. Kept in sync by hand with role_picker.ts (the two open over the same NPC, one
// after the other, so they have to read as one panel).
const RIM = 0xf2a94e
const PANEL_FILL = 0x1b1512
const PANEL_ALPHA = 0.92
const TEXT_COLOR = 0xfff3e0
const BUTTON_IDLE_FILL = 0x2a201a
const BUTTON_HOVER_FILL = 0x33271e
const BUTTON_IDLE_RIM = 0x6b4f38

const PAD = 12
const TITLE_H = 18
const FIELD_H = 26
const GAP = 8
const BUTTON_W = 88
const BUTTON_H = 24
const TAIL_H = 12
const TAIL_HALF_W = 9
const PANEL_CORNER = 10
const BUTTON_CORNER = 6

const PANEL_W = 184
const PANEL_H = PAD + TITLE_H + GAP + FIELD_H + GAP + BUTTON_H + PAD
// Origin (0,0) is the tail tip (points down at the entity); the panel body floats above it.
const PANEL_LEFT = -PANEL_W / 2
const PANEL_TOP = -(TAIL_H + PANEL_H)
const FIELD_W = PANEL_W - 2 * PAD
const FIELD_X = PANEL_LEFT + PAD
const FIELD_Y = PANEL_TOP + PAD + TITLE_H + GAP
const BUTTON_X = -BUTTON_W / 2
const BUTTON_Y = FIELD_Y + FIELD_H + GAP

export interface NamePromptProps {
    // The player confirmed `name` for the bound entity. Never called with an empty string.
    onSubmit?: (name: string) => void
    // The player backed out (Escape). The entity keeps the name it had.
    onCancel?: () => void
}

export class NamePrompt extends Container {
    private onSubmit?: (name: string) => void
    private onCancel?: () => void
    private bg: Graphics
    private titleText: Text
    private input: any
    private button: Container
    private buttonBg: Graphics
    private buttonHovered: boolean = false

    // The entity this prompt is naming. Undefined while closed.
    private targetEntityId?: string

    // Whether the downward pointer is drawn. The prompt stays where it opened while its subject is
    // free to walk off, so the tail is the first thing to stop being true (see setTailVisible).
    private tailVisible: boolean = true

    // Logical open flag -- flips the instant a fade starts so input gating frees immediately
    // (mirrors RolePicker.isOpen semantics). Only one fader runs at a time.
    private opened: boolean = false
    private fadeInFader: Fader | null = null
    private fadeOutFader: Fader | null = null

    constructor(props: NamePromptProps = {}) {
        super()
        this.onSubmit = props.onSubmit
        this.onCancel = props.onCancel

        // Swallow pointer events over the panel so a click on it never falls through to a world
        // attack (the field and button handle their own; this covers the gaps and the title).
        this.interactive = true
        this.hitArea = new Rectangle(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H + TAIL_H)
        this.on("pointerdown", (event: any) => event.stopPropagation())
        this.on("pointermove", (event: any) => event.stopPropagation())
        this.on("rightclick", (event: any) => event.stopPropagation())
        this.on("contextmenu", (event: any) => {
            event.preventDefault()
            event.stopPropagation()
        })

        this.bg = new Graphics()
        this.addChild(this.bg)
        this.drawBackground()

        this.titleText = new TextRenderer()
            .color(TEXT_COLOR)
            .fontSize(13)
            .bold()
            .align("center")
            .render("Name them") as Text
        this.titleText.x = -this.titleText.width / 2
        this.titleText.y = PANEL_TOP + PAD - 2
        this.titleText.alpha = 0.95
        this.addChild(this.titleText)

        this.input = this.makeField()
        this.addChild(this.input)

        const { container, bg } = this.makeButton()
        this.button = container
        this.buttonBg = bg
        this.drawButton()
        this.addChild(this.button)

        this.visible = false
    }

    // Dark "glass" panel with a warm rim, a faint outer glow (a fatter low-alpha copy rather than a
    // blur filter, which matters on low-end GPUs) and a downward pointer whose tip sits at the
    // origin. Drawn exactly as role_picker.ts draws its own, tail and all.
    private drawBackground() {
        const g = this.bg
        g.clear()

        g.beginFill(RIM, 0.06)
        g.drawRoundedRect(PANEL_LEFT - 5, PANEL_TOP - 5, PANEL_W + 10, PANEL_H + 10, PANEL_CORNER + 5)
        g.endFill()

        g.lineStyle(2, RIM, 0.9)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.drawRoundedRect(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H, PANEL_CORNER)
        g.endFill()

        if (!this.tailVisible) {
            return
        }

        // Tail fill (base tucked 1px up into the body so the join is hidden), then only its two
        // visible diagonals stroked.
        const baseY = -TAIL_H - 1
        g.lineStyle(0)
        g.beginFill(PANEL_FILL, PANEL_ALPHA)
        g.moveTo(-TAIL_HALF_W, baseY)
        g.lineTo(0, 0)
        g.lineTo(TAIL_HALF_W, baseY)
        g.closePath()
        g.endFill()

        g.lineStyle(2, RIM, 0.9)
        g.moveTo(-TAIL_HALF_W, baseY)
        g.lineTo(0, 0)
        g.lineTo(TAIL_HALF_W, baseY)
    }

    private makeField() {
        const input = new TextInput({
            input: {
                fontFamily: "Arial, sans-serif",
                fontSize: "15px",
                lineHeight: `${FIELD_H}px`,
                height: `${FIELD_H}px`,
                width: `${FIELD_W}px`,
                boxSizing: "border-box",
                padding: "0 8px",
                textAlign: "center",
                color: "#fff3e0",
                caretColor: "#f2a94e",
                multiline: false,
            },
            box: (w: number, h: number, state: string) => {
                const g = new Graphics()
                g.lineStyle(1.5, state === "FOCUSED" ? RIM : BUTTON_IDLE_RIM, state === "FOCUSED" ? 1 : 0.7)
                g.beginFill(0x120e0b, 0.95)
                g.drawRoundedRect(0, 0, w, h, BUTTON_CORNER)
                g.endFill()
                return g
            },
        })

        // Keep the real <input> on screen instead of the library's pixi Text stand-in: that copy is
        // a canvas raster scaled by the stage transform, and it hides the field from the browser
        // while unfocused. The constructor always builds the stand-in before we can opt out, and
        // _destroySurrogate() leaves _surrogate_mask behind -- an opaque black Graphics that would
        // paint a slab over our frame. Take it out. (Same two fixes as intro/login_field.ts.)
        input.substituteText = false
        const orphanedMask: Graphics | undefined = input._surrogate_mask
        if (orphanedMask) {
            input.removeChild(orphanedMask)
            orphanedMask.destroy()
            input._surrogate_mask = null
        }

        input.x = FIELD_X
        input.y = FIELD_Y
        // The server refuses anything longer anyway (sanitizeEntityName); capping the field means a
        // player never types a name and then watches its tail disappear.
        input.maxLength = ENTITY_NAME_MAX_CHARS

        const dom: HTMLInputElement = input._dom_input
        dom.setAttribute("aria-label", "Name")
        dom.setAttribute("autocapitalize", "words")
        dom.setAttribute("autocorrect", "off")
        dom.setAttribute("spellcheck", "false")

        // Enter accepts, Escape backs out. Both on key-UP because that is the event the library
        // forwards -- and Escape in particular only arrives here while the field has focus, which is
        // why GuiManager also lets Escape through its typing suppression.
        input.on("keyup", (keycode: number) => {
            if (keycode === 13) {
                this.submit()
            }
            if (keycode === 27) {
                this.onCancel?.()
            }
        })

        return input
    }

    private makeButton(): { container: Container; bg: Graphics } {
        const container = new Container()
        container.x = BUTTON_X
        container.y = BUTTON_Y
        container.interactive = true
        container.cursor = "pointer"
        container.hitArea = new Rectangle(0, 0, BUTTON_W, BUTTON_H)

        const bg = new Graphics()
        container.addChild(bg)

        const label = new TextRenderer().color(TEXT_COLOR).fontSize(12).bold().align("center").render("Done")
        label.x = BUTTON_W / 2 - label.width / 2
        label.y = BUTTON_H / 2 - label.height / 2
        container.addChild(label)

        container.on("pointerover", () => {
            this.buttonHovered = true
            this.drawButton()
        })
        container.on("pointerout", () => {
            this.buttonHovered = false
            this.drawButton()
        })
        container.on("pointerdown", (event: any) => {
            event.stopPropagation()
            this.submit()
        })

        return { container, bg }
    }

    private drawButton() {
        const g = this.buttonBg
        if (!g) {
            return
        }
        g.clear()
        if (this.buttonHovered) {
            g.beginFill(RIM, 0.14)
            g.drawRoundedRect(-3, -3, BUTTON_W + 6, BUTTON_H + 6, BUTTON_CORNER + 3)
            g.endFill()
            g.lineStyle(2, RIM, 1)
            g.beginFill(BUTTON_HOVER_FILL, 0.97)
        } else {
            g.lineStyle(1.5, BUTTON_IDLE_RIM, 0.7)
            g.beginFill(BUTTON_IDLE_FILL, 0.9)
        }
        g.drawRoundedRect(0, 0, BUTTON_W, BUTTON_H, BUTTON_CORNER)
        g.endFill()
    }

    /**
     * Show or hide the DOM <input> itself.
     *
     * pixi-text-input syncs the real <input>'s position AND its css display from render(), i.e.
     * only while the panel is actually being drawn. So the moment this container's `visible` goes
     * false, pixi stops drawing it and the library never gets another chance to hide the field --
     * leaving the typed name on the page for good: crisp, fixed in screen space, floating over
     * whatever the player does next. (It bites anything that is hidden before it is torn down --
     * the intro's login card hides itself the moment you press Play and only disposes once the
     * server answers, so intro/login_field.ts carries the same pair of methods.)
     *
     * So the prompt drives it itself, around every open and close.
     */
    private setFieldVisible(visible: boolean) {
        this.input._setDOMInputVisible(visible)
    }

    // An empty field means "leave the name alone" rather than "clear the name", so it cancels.
    private submit() {
        const value = (this.input.text || "").trim()
        if (!value) {
            this.onCancel?.()
            return
        }
        this.onSubmit?.(value)
    }

    // Bind the prompt to one entity and start the field on the name it already has. The focus is
    // deferred a tick on purpose: a focus() issued while the browser is still dispatching the
    // pointerdown that led here is handed straight back to the canvas, leaving the player typing at
    // the game instead of the field (the trap intro/login_field.ts hit).
    public bindToEntity(targetEntityId: string, name?: string) {
        this.targetEntityId = targetEntityId
        this.input.text = name || ""
        this.buttonHovered = false
        this.drawButton()
        setTimeout(() => {
            if (this.opened) {
                // select(), not focus(): the field opens on a name already, and the player's first
                // keystroke should replace it rather than land in the middle of it.
                this.input.select()
            }
        }, 0)
    }

    public clearTarget() {
        this.targetEntityId = undefined
        this.input.blur()
    }

    // Show or hide the downward pointer. This prompt is pinned where it opened on purpose, so the
    // member being named routinely walks out from under it -- and a tail hanging over the spot they
    // left points at bare ground, or at whoever stepped into it. Dropping it leaves a plain panel,
    // which is the honest thing to show. The hit area follows so the widget stops swallowing clicks
    // on a strip of world it no longer covers.
    public setTailVisible(visible: boolean) {
        if (this.tailVisible === visible) {
            return
        }
        this.tailVisible = visible
        this.hitArea = new Rectangle(PANEL_LEFT, PANEL_TOP, PANEL_W, PANEL_H + (visible ? TAIL_H : 0))
        this.drawBackground()
    }

    public getTargetEntityId(): string | undefined {
        return this.targetEntityId
    }

    public isOpen(): boolean {
        return this.opened
    }

    // Reveal by fading in, interrupting an in-progress dismiss (mirrors RolePicker.fadeIn).
    public fadeIn() {
        this.opened = true
        // Shown on the NEXT frame, not this one: the library places the DOM input from its own
        // render(), so a field made visible before that first render is briefly drawn wherever the
        // page would have put it -- a flash of the name in the top-left corner. Still done by hand
        // rather than left to the library, because a reopen that interrupts a dismissal can leave
        // the panel's transform unchanged, and the library only re-syncs when something about it
        // has changed -- which would leave the field hidden for good.
        requestAnimationFrame(() => {
            if (this.opened) {
                this.setFieldVisible(true)
            }
        })

        if (this.fadeOutFader) {
            this.fadeOutFader.stop(true)
            this.fadeOutFader = null
        }
        if (this.fadeInFader) {
            return
        }

        if (!this.visible) {
            this.alpha = 0
        }
        this.visible = true

        if (this.alpha >= 1) {
            this.alpha = 1
            return
        }

        this.fadeInFader = new Fader(this, {
            direction: "in",
            minAlpha: 1.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeInFader = null
                this.alpha = 1
            },
        })
        this.fadeInFader.update()
    }

    // Dismiss by fading out, hiding once transparent (mirrors RolePicker.fadeOut). The DOM input
    // follows worldVisible/worldAlpha, so it goes with it.
    public fadeOut() {
        this.opened = false
        this.buttonHovered = false
        this.drawButton()
        this.input.blur()
        // Before the early returns below, and before the fade rather than at the end of it: every
        // path out of here has to take the field with it (see setFieldVisible).
        this.setFieldVisible(false)

        if (this.fadeInFader) {
            this.fadeInFader.stop(true)
            this.fadeInFader = null
        }
        if (this.fadeOutFader || !this.visible) {
            return
        }

        this.fadeOutFader = new Fader(this, {
            direction: "out",
            minAlpha: 0.0,
            fadeInterval: FADE_INTERVAL_MS,
            fadeAlphaDecrement: FADE_STEP,
            releaseCallback: () => {
                this.fadeOutFader = null
                this.visible = false
                this.alpha = 1.0
                this.setFieldVisible(false)
            },
        })
        this.fadeOutFader.update()
    }
}
